import { zodResolver } from '@hookform/resolvers/zod';
import EditIcon from "@mui/icons-material/Edit";
import { Box, Collapse, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Control, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { EntityWithIdFields } from 'src/types';
import { dateFormatter } from 'src/utils/formatters';
import { numberFormatter } from "../../../../../utils/formatters/numberFormatter";
import { HttpLCLS } from '../api/httpLCLS';
import {
  OperacionLCLSCuentaLimiteUpdate,
  OperacionLCLSCuentaLimiteVista,
  OperacionLCLSLimiteTipo, schemaOperacionLCLSCuentaLimiteUpdate,
} from '../types/dto';
import { OperacionLCLSCuentaLimiteFields } from "../types/fields";
import { OperacionLCLS } from '../types/types';
import { OperacionLCLSCampoLimite } from './OperacionLCLSCampoLimite';

const ACTION_WIDTH = 40;

export const LimitCellLayout = ({ children, action }: { children: React.ReactNode, action?: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
    <Box sx={{ textAlign: 'right', flexGrow: 1 }}>{children}</Box>
    <Box sx={{ width: ACTION_WIDTH, minWidth: ACTION_WIDTH, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
      {action || null}
    </Box>
  </Box>
);

interface EditableLCLSCampoLimiteProps {
  isEditing: boolean;
  onEdit: () => void;
  value: number | null;
  label: string;
  name: string;
  control: Control<any>;
  loadOptions: () => Promise<OperacionLCLSLimiteTipo[]>;
  onModeChange: (isFreeSolo: boolean) => void;
  disabled?: boolean;
  permiteEditar: boolean;
}

const EditableLCLSCampoLimite = ({
  isEditing, onEdit, value, label, name, control, loadOptions, onModeChange, disabled, permiteEditar = false
}: EditableLCLSCampoLimiteProps) => {
  if (!isEditing) {
    return (
      <LimitCellLayout
        action={
          <IconButton onClick={onEdit} size="small" disabled={disabled}>
            <EditIcon fontSize="small" color={disabled ? 'disabled' : 'info'} />
          </IconButton>
        }
      >
        <Typography variant="body2" noWrap>
          {numberFormatter.toStringWithAmount(value, 'USD', 0)}
        </Typography>
      </LimitCellLayout>
    );
  }

  return (
    <OperacionLCLSCampoLimite
      control={control}
      name={name}
      label={label}
      loadOptions={loadOptions}
      onModeChange={onModeChange}
      buttonType={'icon'}
      disabled={disabled}
      permiteEditar={permiteEditar}
    />
  );
};

interface OperacionLCLSLimiteFilaProps {
  operacion: OperacionLCLS;
  limite: OperacionLCLSCuentaLimiteVista;
  loadLimitesOptions: () => Promise<OperacionLCLSLimiteTipo[]>;
  refetch: () => void;
  isHistoryRow?: boolean;
  isExpanded?: boolean;
  permiteEditar: boolean;
}

export const OperacionLCLSLimiteFila = ({
  operacion, limite, loadLimitesOptions, refetch, isHistoryRow = false, isExpanded = true, permiteEditar = false
}: OperacionLCLSLimiteFilaProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [fieldModes, setFieldModes] = useState({ concertacion: false, dvp: false, fop: false });
  const [editModes, setEditModes] = useState({ concertacion: false, dvp: false, fop: false });

  // const cellSx = isHistoryRow ? {
  //   paddingY: isExpanded ? '6px !important' : '0 !important',
  //   lineHeight: isExpanded ? 1.5 : 0,
  //   borderBottom: isExpanded ? undefined : '0px solid transparent !important',
  //   transition: 'all 0.3s ease-in-out',
  //   overflow: 'hidden',
  // } : { paddingY: '6px' };

  const cellSx = isHistoryRow ? {
    paddingY: isExpanded ? '6px !important' : '0 !important',
    borderBottom: isExpanded ? undefined : '0px solid transparent !important',
    overflow: 'hidden',
  } : { paddingY: '6px' };

  // const cellSx = { paddingY: '6px' }

  const formMethods = useForm<OperacionLCLSCuentaLimiteUpdate>({
    resolver: zodResolver(schemaOperacionLCLSCuentaLimiteUpdate),
    defaultValues: {
      [OperacionLCLSCuentaLimiteFields.LimiteConcertacion]: limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion],
      [OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP]: limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP],
      [OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP]: limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP],
    },
  });

  const { control, handleSubmit, formState: { isDirty }, reset } = formMethods;
  const watchedValues = formMethods.watch();

  const handleUpdate: SubmitHandler<OperacionLCLSCuentaLimiteUpdate> = (data) => {
    setIsSaving(true);
    const promise = HttpLCLS.updateLimite(operacion[EntityWithIdFields.Id], limite[EntityWithIdFields.Id], data);
    toast.promise(promise, { loading: 'Guardando...', success: 'Límite actualizado.', error: (e) => `Error: ${e}` })
      .then(() => {
        refetch();
        reset(data);
        setEditModes({ concertacion: false, dvp: false, fop: false });
      })
      .finally(() => setIsSaving(false));
  };

  useEffect(() => {
    // @ts-ignore
    if (!isDirty || Object.values(watchedValues).includes(null)) return;
    const useDebounce = fieldModes.concertacion || fieldModes.dvp || fieldModes.fop;
    const timer = useDebounce ? setTimeout(() => handleSubmit(handleUpdate)(), 1000) : null;
    if (!useDebounce) handleSubmit(handleUpdate)();
    
return () => { if (timer) clearTimeout(timer); };
  }, [JSON.stringify(watchedValues)]);

  const isReadOnly = !(limite[OperacionLCLSCuentaLimiteFields.PermiteModificar] && permiteEditar);

  return (
    <TableRow sx={{ height: isHistoryRow && !isExpanded ? 0 : 'auto' }}>
      <TableCell sx={cellSx}>
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ py: 0.5 }}>{limite[OperacionLCLSCuentaLimiteFields.DescGrupo]}</Box>
        </Collapse>
      </TableCell>

      <TableCell align="right" sx={cellSx}>
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ py: 0.5 }}>
            {isReadOnly ? (
              <LimitCellLayout>
                <Typography variant="body2">{numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion], 'USD', 0)}</Typography>
              </LimitCellLayout>
            ) : (
              <EditableLCLSCampoLimite
                isEditing={editModes.concertacion}
                onEdit={() => setEditModes(prev => ({ ...prev, concertacion: true }))}
                value={limite[OperacionLCLSCuentaLimiteFields.LimiteConcertacion]}
                label="Límite de concertación"
                name={OperacionLCLSCuentaLimiteFields.LimiteConcertacion}
                control={control}
                loadOptions={loadLimitesOptions}
                onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, concertacion: isFreeSolo }))}
                disabled={isSaving}
                permiteEditar={!isReadOnly}
              />
            )}
          </Box>
        </Collapse>
      </TableCell>

      <TableCell align="right" sx={cellSx}>
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ py: 0.5 }}>
            {isReadOnly ? (
              <LimitCellLayout>
                <Typography variant="body2">{numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP], 'USD', 0)}</Typography>
              </LimitCellLayout>
            ) : (
              <EditableLCLSCampoLimite
                isEditing={editModes.dvp}
                onEdit={() => setEditModes(prev => ({ ...prev, dvp: true }))}
                value={limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP]}
                label="Límite de liquidación DVP"
                name={OperacionLCLSCuentaLimiteFields.LimiteLiquidacionDVP}
                control={control}
                loadOptions={loadLimitesOptions}
                onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, dvp: isFreeSolo }))}
                disabled={isSaving}
                permiteEditar={!isReadOnly}
              />
            )}
          </Box>
        </Collapse>
      </TableCell>

      <TableCell align="right" sx={cellSx}>
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ py: 0.5 }}>
            {isReadOnly ? (
              <LimitCellLayout>
                <Typography variant="body2">{numberFormatter.toStringWithAmount(limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP], 'USD', 0)}</Typography>
              </LimitCellLayout>
            ) : (
              <EditableLCLSCampoLimite
                isEditing={editModes.fop}
                onEdit={() => setEditModes(prev => ({ ...prev, fop: true }))}
                value={limite[OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP]}
                label="Límite de liquidación FOP"
                name={OperacionLCLSCuentaLimiteFields.LimiteLiquidacionFOP}
                control={control}
                loadOptions={loadLimitesOptions}
                onModeChange={(isFreeSolo) => setFieldModes(prev => ({ ...prev, fop: isFreeSolo }))}
                disabled={isSaving}
                permiteEditar={!isReadOnly}
              />
            )}
          </Box>
        </Collapse>
      </TableCell>

      <TableCell sx={cellSx}>
        <Collapse in={isExpanded} timeout={300}>
          <Box sx={{ py: 0.5 }}>{dateFormatter.toShortDate(isReadOnly ? limite[OperacionLCLSCuentaLimiteFields.FechaAccion] : new Date())}</Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};